/**
 * Utility Import Modal
 * Import utility markers from paste data or CSV
 *
 * Supports format:
 * - Category headers (rows with empty lat/lng)
 * - Utility rows: STT, Name, Lat, Lng
 *
 * Auto-detects Vietnamese category names and maps to icon_type:
 * - "Trường học" → school
 * - "Bệnh viện" / "Y tế" → hospital
 * - "Giao thông" / "Bến xe" → bus_station
 * - "Ngân hàng" → bank
 * - "Công viên" → park
 * - etc.
 */
import { useState } from 'react'
import {
  HiOutlineX,
  HiOutlineCheck,
  HiOutlineExclamation,
  HiOutlineTrash,
  HiOutlinePencil,
  HiOutlineClipboardList,
} from 'react-icons/hi'
import clsx from 'clsx'
import { UTILITY_ICONS } from '../map/LeafletMarketMapEditor'

// Map Vietnamese category names to icon_type
const CATEGORY_TO_ICON = {
  // Education
  'trường học': 'school',
  'trường': 'school',
  'giáo dục': 'school',
  'tiểu học': 'school',
  'thpt': 'school',
  'thcs': 'school',
  'đại học': 'school',
  'mầm non': 'school',

  // Healthcare
  'y tế': 'hospital',
  'bệnh viện': 'hospital',
  'phòng khám': 'hospital',
  'trạm y tế': 'hospital',

  // Transportation
  'giao thông': 'bus_station',
  'vận tải': 'bus_station',
  'bến xe': 'bus_station',
  'sân bay': 'airport',
  'ga tàu': 'train_station',
  'cảng': 'port',

  // Finance
  'ngân hàng': 'bank',
  'tài chính': 'bank',
  'atm': 'bank',

  // Commerce
  'thương mại': 'market',
  'dịch vụ': 'market',
  'chợ': 'market',
  'siêu thị': 'supermarket',
  'cửa hàng': 'supermarket',

  // Recreation
  'công viên': 'park',
  'giải trí': 'park',
  'thể thao': 'gym',
  'sân vận động': 'stadium',

  // Government
  'hành chính': 'government',
  'công an': 'police',
  'ubnd': 'government',

  // Religion
  'nhà thờ': 'church',
  'chùa': 'temple',

  // Other
  'khách sạn': 'hotel',
  'nhà hàng': 'restaurant',
  'cafe': 'cafe',
  'cây xăng': 'gas_station',
  'bãi đỗ': 'parking',
  'nhà thuốc': 'pharmacy',
}

// Detect icon_type from Vietnamese category name
const detectIconType = (categoryName) => {
  if (!categoryName) return 'default'

  const lowerName = categoryName.toLowerCase()

  for (const [keyword, iconType] of Object.entries(CATEGORY_TO_ICON)) {
    if (lowerName.includes(keyword)) {
      return iconType
    }
  }

  return 'default'
}

// Parse coordinate value
const parseCoordinate = (value) => {
  if (!value) return null
  if (typeof value === 'number') return value

  const str = value.toString().trim()
  const num = parseFloat(str.replace(',', '.'))
  return isNaN(num) ? null : num
}

// Parse pasted data with category headers
const parsePastedData = (text) => {
  const lines = text.trim().split('\n').filter(line => line.trim())
  const parsedData = []
  const errors = []
  let currentCategory = 'Tiện ích'
  let currentIconType = 'default'

  lines.forEach((line, index) => {
    const rowNum = index + 1

    // Split by comma or tab
    const parts = line.split(/[,\t]/).map(p => p.trim())

    if (parts.length < 2) {
      return // Skip empty or invalid lines
    }

    // Check if this is a header row (STT, Tên, Lat, Lon)
    const firstLower = parts[0].toLowerCase()
    if (firstLower === 'stt' || firstLower === '#' || firstLower === 'tên') {
      return // Skip header row
    }

    // Check if first part is number (STT) or category header
    const firstIsNumber = /^\d+$/.test(parts[0])

    // If no lat/lng values (empty or missing), this is a category header
    const hasCoordinates = parts.length >= 4 &&
      parts[2] && parts[3] &&
      parseCoordinate(parts[2]) !== null &&
      parseCoordinate(parts[3]) !== null

    if (!hasCoordinates && !firstIsNumber) {
      // This is a category header row
      currentCategory = parts[0] || parts[1] || 'Tiện ích'
      currentIconType = detectIconType(currentCategory)
      return
    }

    // Parse utility row
    let name, lat, lng

    if (firstIsNumber && parts.length >= 4) {
      // Format: STT, Name, Lat, Lng
      name = parts[1]
      lat = parseCoordinate(parts[2])
      lng = parseCoordinate(parts[3])
    } else if (parts.length >= 3) {
      // Format: Name, Lat, Lng
      name = parts[0]
      lat = parseCoordinate(parts[1])
      lng = parseCoordinate(parts[2])
    } else {
      errors.push(`Dòng ${rowNum}: Thiếu dữ liệu`)
      return
    }

    if (!name) {
      errors.push(`Dòng ${rowNum}: Thiếu tên`)
      return
    }

    if (lat === null || lng === null) {
      errors.push(`Dòng ${rowNum}: Tọa độ không hợp lệ`)
      return
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      errors.push(`Dòng ${rowNum}: Tọa độ nằm ngoài phạm vi`)
      return
    }

    parsedData.push({
      _rowNum: rowNum,
      _category: currentCategory,
      name: name.trim(),
      latitude: lat,
      longitude: lng,
      icon_type: currentIconType,
      is_valid: true,
    })
  })

  return { data: parsedData, errors }
}

function UtilityImportModal({ isOpen, onClose, onImport }) {
  const [mode, setMode] = useState('paste') // 'paste' | 'preview'
  const [pasteText, setPasteText] = useState('')
  const [previewData, setPreviewData] = useState([])
  const [errors, setErrors] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [editingRow, setEditingRow] = useState(null)

  const resetState = () => {
    setMode('paste')
    setPasteText('')
    setPreviewData([])
    setErrors([])
    setEditingRow(null)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handlePasteSubmit = () => {
    if (!pasteText.trim()) {
      setErrors(['Vui lòng dán dữ liệu vào ô nhập'])
      return
    }

    setIsLoading(true)
    setErrors([])

    try {
      const { data, errors: parseErrors } = parsePastedData(pasteText)
      setPreviewData(data)
      setErrors(parseErrors)
      setMode('preview')
    } catch (error) {
      console.error('Parse error:', error)
      setErrors(['Không thể đọc dữ liệu. Vui lòng kiểm tra định dạng.'])
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveRow = (index) => {
    setPreviewData(prev => prev.filter((_, i) => i !== index))
  }

  const handleEditRow = (index) => {
    setEditingRow(index)
  }

  const handleSaveEdit = (index, updatedRow) => {
    setPreviewData(prev => prev.map((row, i) => i === index ? { ...row, ...updatedRow } : row))
    setEditingRow(null)
  }

  const handleCancelEdit = () => {
    setEditingRow(null)
  }

  const handleImport = async () => {
    if (previewData.length === 0) return

    setImporting(true)
    try {
      const markers = previewData.map(({ _rowNum, _category, is_valid, ...marker }) => marker)
      await onImport(markers)
      handleClose()
    } catch (error) {
      console.error('Import error:', error)
      setErrors(['Lỗi khi import. Vui lòng thử lại.'])
    } finally {
      setImporting(false)
    }
  }

  // Group preview data by category
  const groupedData = previewData.reduce((acc, item) => {
    const cat = item._category || 'Khác'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(item)
    return acc
  }, {})

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span className="text-xl">🏫</span>
              Import Tiện ích
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {mode === 'paste' && 'Dán dữ liệu tiện ích từ Excel/Sheets'}
              {mode === 'preview' && `Xem trước ${previewData.length} tiện ích`}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <HiOutlineX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Mode: Paste */}
          {mode === 'paste' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dán dữ liệu tiện ích
                </label>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder={`Trường học các cấp,,,
1,Trường Tiểu học Kim Đồng,16.390166,107.58785
2,Trường THCS Nguyễn Tri Phương,16.395024,107.59201

Y tế & Bệnh viện,,,
1,Bệnh viện Trung ương Huế,16.479139,107.60167
...`}
                  className="w-full h-64 p-3 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2">
                  Format: STT, Tên, Lat, Lng. Dòng không có tọa độ được nhận là tiêu đề nhóm (category).
                </p>
              </div>

              {/* Format guide */}
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-900 mb-2">Định dạng được hỗ trợ</h4>
                <div className="text-sm text-emerald-700 space-y-1">
                  <p><span className="font-mono bg-emerald-100 px-1 rounded">Trường học,,,</span> → Nhóm tiện ích (icon: 🏫)</p>
                  <p><span className="font-mono bg-emerald-100 px-1 rounded">1,Tên trường,16.39,107.58</span> → Tiện ích</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-white rounded text-xs">🏫 Trường học</span>
                  <span className="px-2 py-1 bg-white rounded text-xs">🏥 Bệnh viện</span>
                  <span className="px-2 py-1 bg-white rounded text-xs">🚌 Giao thông</span>
                  <span className="px-2 py-1 bg-white rounded text-xs">🏦 Ngân hàng</span>
                  <span className="px-2 py-1 bg-white rounded text-xs">🏪 Thương mại</span>
                  <span className="px-2 py-1 bg-white rounded text-xs">🌳 Công viên</span>
                </div>
              </div>

              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <HiOutlineExclamation className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      {errors.map((err, i) => (
                        <p key={i}>{err}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="btn-outline"
                >
                  Hủy
                </button>
                <button
                  onClick={handlePasteSubmit}
                  disabled={!pasteText.trim() || isLoading}
                  className="btn-primary flex-1"
                  style={{ backgroundColor: '#10B981' }}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner w-4 h-4 mr-2" />
                      Đang xử lý...
                    </>
                  ) : (
                    <>
                      <HiOutlineCheck className="w-4 h-4 mr-1" />
                      Xem trước
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Mode: Preview */}
          {mode === 'preview' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between bg-emerald-50 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center text-xl">
                    🏫
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {previewData.length} tiện ích hợp lệ
                    </p>
                    <p className="text-sm text-gray-600">
                      {Object.keys(groupedData).length} nhóm
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setMode('paste')
                    setPreviewData([])
                    setErrors([])
                  }}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Nhập lại
                </button>
              </div>

              {/* Errors */}
              {errors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <HiOutlineExclamation className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-700">
                      <p className="font-medium mb-1">Một số dòng có lỗi:</p>
                      <ul className="list-disc list-inside space-y-0.5 max-h-20 overflow-y-auto">
                        {errors.slice(0, 5).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {errors.length > 5 && (
                          <li className="text-amber-600">...và {errors.length - 5} lỗi khác</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview by category */}
              {Object.entries(groupedData).map(([category, items]) => (
                <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{UTILITY_ICONS[items[0]?.icon_type] || '📍'}</span>
                      <span className="font-medium text-gray-700">{category}</span>
                      <span className="text-xs text-gray-500">({items.length})</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto max-h-48">
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-gray-100">
                        {items.map((row, idx) => {
                          const globalIndex = previewData.findIndex(d => d === row)
                          return (
                            <tr key={idx} className="hover:bg-gray-50">
                              {editingRow === globalIndex ? (
                                <EditableRow
                                  row={row}
                                  index={globalIndex}
                                  onSave={handleSaveEdit}
                                  onCancel={handleCancelEdit}
                                />
                              ) : (
                                <>
                                  <td className="px-3 py-2 text-gray-500 w-10">{idx + 1}</td>
                                  <td className="px-3 py-2 font-medium text-gray-900">{row.name}</td>
                                  <td className="px-3 py-2 font-mono text-xs text-gray-500 w-48">
                                    {row.latitude.toFixed(6)}, {row.longitude.toFixed(6)}
                                  </td>
                                  <td className="px-3 py-2 w-20">
                                    <div className="flex gap-1">
                                      <button
                                        onClick={() => handleEditRow(globalIndex)}
                                        className="p-1 hover:bg-blue-100 rounded text-blue-500"
                                        title="Sửa"
                                      >
                                        <HiOutlinePencil className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleRemoveRow(globalIndex)}
                                        className="p-1 hover:bg-red-100 rounded text-red-500"
                                        title="Xóa"
                                      >
                                        <HiOutlineTrash className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {previewData.length === 0 && (
                <div className="py-8 text-center text-gray-500">
                  <p>Không có dữ liệu hợp lệ để import</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <p className="text-sm text-gray-500">
            {mode === 'preview' && previewData.length > 0 && (
              `Sẵn sàng import ${previewData.length} tiện ích`
            )}
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Hủy
            </button>
            {mode === 'preview' && (
              <button
                onClick={handleImport}
                disabled={previewData.length === 0 || importing}
                className={clsx(
                  'px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2',
                  previewData.length > 0 && !importing
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-gray-400 cursor-not-allowed'
                )}
              >
                {importing ? (
                  <>
                    <span className="spinner w-4 h-4" />
                    Đang import...
                  </>
                ) : (
                  <>
                    <HiOutlineCheck className="w-4 h-4" />
                    Import {previewData.length} tiện ích
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Editable Row Component
function EditableRow({ row, index, onSave, onCancel }) {
  const [editData, setEditData] = useState({
    name: row.name,
    latitude: row.latitude.toString(),
    longitude: row.longitude.toString(),
    icon_type: row.icon_type,
  })

  const handleSave = () => {
    const lat = parseFloat(editData.latitude)
    const lng = parseFloat(editData.longitude)

    if (!editData.name.trim()) {
      alert('Vui lòng nhập tên')
      return
    }
    if (isNaN(lat) || isNaN(lng)) {
      alert('Tọa độ không hợp lệ')
      return
    }

    onSave(index, {
      ...row,
      name: editData.name.trim(),
      latitude: lat,
      longitude: lng,
      icon_type: editData.icon_type,
    })
  }

  return (
    <>
      <td className="px-3 py-2 text-gray-500">{index + 1}</td>
      <td className="px-2 py-1">
        <input
          type="text"
          value={editData.name}
          onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500"
          autoFocus
        />
      </td>
      <td className="px-2 py-1">
        <div className="flex gap-1">
          <input
            type="text"
            value={editData.latitude}
            onChange={(e) => setEditData(prev => ({ ...prev, latitude: e.target.value }))}
            className="w-24 px-2 py-1 text-xs font-mono border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500"
            placeholder="Lat"
          />
          <input
            type="text"
            value={editData.longitude}
            onChange={(e) => setEditData(prev => ({ ...prev, longitude: e.target.value }))}
            className="w-24 px-2 py-1 text-xs font-mono border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500"
            placeholder="Lng"
          />
        </div>
      </td>
      <td className="px-2 py-1">
        <div className="flex gap-1">
          <button
            onClick={handleSave}
            className="p-1 bg-green-100 hover:bg-green-200 rounded text-green-600"
            title="Lưu"
          >
            <HiOutlineCheck className="w-4 h-4" />
          </button>
          <button
            onClick={onCancel}
            className="p-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-600"
            title="Hủy"
          >
            <HiOutlineX className="w-4 h-4" />
          </button>
        </div>
      </td>
    </>
  )
}

export default UtilityImportModal
